<template>
    <div>
        <scatter-chart :chart-data="chartData" :options="chartOptions" ref="scatterChart"></scatter-chart>
        <div>
            <div class="d-inline-block" style="margin-right: 10px" v-for="coloring in realEstateColorings.filter(x => x.name)">
                <i class="fas fa-square" :style="`color: ${coloring.color}`"></i> {{ coloring.name }} <template v-if="coloring.name === 'újszerű'">/ újépítésű</template>
            </div>
        </div>
        <div class="form-group mt-3">
            <label>Összehasonlító ingatlanok száma</label>
            <input class="form-control" type="number" step="1" v-model.number="referenceRealEstatesCount">
        </div>
        <hr>
        <div>
            <div class="d-inline-block" style="width: 70px">
                <VueToggles :value="filterOutliers" @click="filterOutliers = !filterOutliers" height="25" width="60" checkedBg="#198754"></VueToggles>
            </div>
            <span style="font-size: 19px">Szélsőséges értékek kizárása ({{ realEstates.length - applyOutlierFilter(realEstates).length }} ingatlan)</span>
        </div>
        <div class="form-group" v-if="filterOutliers">
            <div class="btn-group d-block">
                <button type="button" class="btn" :class="`btn-${outlierAvgDistanceMultiplier === option.value ? 'dark' : 'secondary'}`" @click="outlierAvgDistanceMultiplier = option.value" v-for="option in outlierAvgDistanceMultiplierOptions">{{ option.name }}</button>
            </div>
        </div>
        <hr>
        <div>
            <div class="d-inline-block" style="width: 70px">
                <VueToggles :value="filterDuplications" @click="filterDuplications = !filterDuplications" height="25" width="60" checkedBg="#198754"></VueToggles>
            </div>
            <span style="font-size: 19px">Duplikációk kizárása ({{ realEstates.length - applyDuplicationFilter(realEstates).length }} ingatlan)</span>
        </div>
    </div>
</template>

<script>
import ScatterChart from './ScatterChart';
import regression from 'regression';

const million = 1000000;
const calcRegression = realEstates => regression.linear(realEstates.map(x => [x.area, x.price / million]));

export default {
    name: 'ReferenceRealEstatesChart',
    components: { ScatterChart },
    data: function () {
        return {
            maxRegressionPoints: 300,
            referenceRealEstatesCount: 6,
            outlierAvgDistanceMultiplier: 3,
            filterOutliers: true,
            filterDuplications: true,
            outlierAvgDistanceMultiplierOptions: [
                { name: 'Normál', value: 3 },
                { name: 'Közepes', value: 2 },
                { name: 'Erős', value: 1 },
            ],
            realEstateColorings: [
                { name: 'újszerű', color: '#87ff00' },
                { name: 'felújított', color: '#e1ff00' },
                { name: 'jó állapotú', color: '#ffd900' },
                { name: 'közepes állapotú', color: '#ff9600' },
                { name: 'felújítandó', color: '#ff5200' },
                { name: null, color: '#ff0000' },
            ],
        };
    },
    props: ['realEstates', 'ownRealEstate'],
    computed: {
        filteredRealEstates: function () {
            let realEstates = this.realEstates;
            if (this.filterOutliers) {
                realEstates = this.applyOutlierFilter(realEstates);
            }
            if (this.filterDuplications) {
                realEstates = this.applyDuplicationFilter(realEstates);
            }
            return realEstates;
        },
        minArea: function () {
            return Math.min(...this.filteredRealEstates.map(x => x.area), this.ownRealEstate.area);
        },
        maxArea: function () {
            return Math.max(...this.filteredRealEstates.map(x => x.area), this.ownRealEstate.area);
        },
        regressionResult: function () {
            return calcRegression(this.filteredRealEstates);
        },
        validReferenceRealEstatesCount: function () {
            return this.referenceRealEstatesCount && Number.isInteger(this.referenceRealEstatesCount) && this.referenceRealEstatesCount > 0 ? this.referenceRealEstatesCount : 0;
        },
        selectedRealEstates: function () {
            let area = this.ownRealEstate.area;
            let price = this.predictPrice(area);

            let calculateDiff = x => Math.abs(area - x.area) * 5 + Math.abs(price - x.price / million);
            let sortedRealEstates = [...this.filteredRealEstates].sort((a, b) => calculateDiff(a) - calculateDiff(b));

            return sortedRealEstates.slice(0, this.validReferenceRealEstatesCount);
        },
        realEstateColors: function () {
            return this.filteredRealEstates.map(realEstate => {
                if (!this.selectedRealEstates.includes(realEstate)) return '#e8e8e8';
                let coloring = this.realEstateColorings.find(x => x.name === realEstate.condition) || this.realEstateColorings.find(x => x.name === null);
                return coloring.color;
            });
        },
        chartData: function () {
            let regressionResolution = (this.maxArea - this.minArea) / this.maxRegressionPoints;
            if (regressionResolution < 0.1) regressionResolution = 0.1;
            let regressionData = [];
            for (let area = this.minArea; area <= this.maxArea; area += regressionResolution) {
                regressionData.push(this.getPredictedPoint(area));
            }

            return {
                datasets: [
                    {
                        label: 'Tárgyingatlan',
                        data: [this.getPredictedPoint(this.ownRealEstate.area)],
                        pointRadius: 10,
                        backgroundColor: '#C17AD1',
                        borderColor: '#C17AD1',
                    }, {
                        label: `Ingatlanok (${this.filteredRealEstates.length})`,
                        data: this.filteredRealEstates.map(r => ({ x: r.area, y: r.price / million })),
                        pointBackgroundColor: this.realEstateColors,
                        pointRadius: 4,
                    }, {
                        label: 'Becsült árak',
                        data: regressionData,
                        pointRadius: 2,
                        backgroundColor: '#36a2eb88',
                        borderColor: '#36a2eb',
                    },
                ],
            };
        },
        chartOptions: function () {
            return {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    xAxes: [{
                        scaleLabel: {
                            display: true,
                            labelString: 'Négyzetméter',
                            fontStyle: 'bold',
                        },
                    }],
                    yAxes: [{
                        scaleLabel: {
                            display: true,
                            labelString: 'Ár (MFt)',
                            fontStyle: 'bold',
                        },
                    }],
                },
                tooltips: {
                    callbacks: {
                        label: (item, data) => {
                            let label = [`${Math.round(item.xLabel)} nm`, `${Math.round(item.yLabel)} MFt`];
                            if (item.datasetIndex === 1) {
                                let realEstate = this.filteredRealEstates[item.index];
                                label.push(realEstate.condition || 'állapot nincs megadva');
                            }
                            return label;
                        },
                    }
                },
                onClick: (event, elements) => {
                    for (let element of elements) {
                        if (element._datasetIndex === 1) {
                            let realEstate = this.filteredRealEstates[element._index];
                            window.open(realEstate.url);
                        }
                    }
                },
                animation: {
                    duration: 0,
                },
            };
        },
    },
    watch: {
        'selectedRealEstates': {
            handler: function () {
                this.$emit('changed', this.selectedRealEstates);
            },
            immediate: true,
        },
        'realEstateColors': function () {
            let chart = this.$refs.scatterChart.$data._chart;
            chart.data.datasets[1].pointBackgroundColor = this.realEstateColors;
            chart.update();
        },
    },
    methods: {
        predictPrice: function (area) {
            return this.regressionResult.predict(area)[1];
        },
        getPredictedPoint: function (area) {
            return { x: area, y: this.predictPrice(area) };
        },
        applyOutlierFilter: function (realEstates) {
            let regressionResult = calcRegression(this.realEstates);
            let distances = realEstates.map(x => Math.abs(regressionResult.predict(x.area)[1] - x.price / million));
            let avgDistance = avg(distances);
            return realEstates.filter((x, i) => distances[i] <= avgDistance * this.outlierAvgDistanceMultiplier);
        },
        applyDuplicationFilter: function (realEstates) {
            return realEstates.filter(x => x === realEstates.find(y => x.area === y.area && x.price === y.price));
        },
    },
}
</script>
